import { Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Observable, ReplaySubject, startWith } from 'rxjs';
import { AppError, FileResult } from '@imagise/entities';
import { EditMode } from './enums/editor.enum';
import {
  EditorAnimationDraftOperation,
  EditorAnimationFrame,
  EditorCanvasViewState,
  EditorCommittedEdit,
  EditorDraftOperation,
  EditorFormValue,
  EditorMergeDraftOperation,
  EditorMergeImage,
  EditorMergeParams,
  EditorPreparedCommit,
  cloneCommittedEdits,
  cloneDraftOperation,
  cloneEditorFormValue,
  createEmptyEditorFormValue,
  getAppliedImageSize,
  getEditorCanvasViewState,
  isMeaningfulMergeDraft,
} from './editor-domain.models';
import {
  createEditorSourceImageState,
  toEditorDraftOperation,
  toPreparedCommit,
  withCropDraftRect,
} from './editor-operation.adapter';
import {
  appendAnimationFrames,
  deleteAnimationFrame,
  duplicateAnimationFrame,
  moveAnimationFrame,
  setAnimationFrameDuration,
} from './animation-frames.utils';
import {
  appendMergeImages,
  deleteMergeImage,
  duplicateMergeImage,
  moveMergeImage,
  setMergeImagePosition,
  setMergeImagePositions,
} from './merge-images.utils';

type EditorSourceImageControls = {
  fileId: FormControl<string | null>;
  fileName: FormControl<string>;
  sourceBase64: FormControl<string | null>;
  mimeType: FormControl<string | null>;
  previewUrl: FormControl<string | null>;
  width: FormControl<number | null>;
  height: FormControl<number | null>;
};

type EditorOutputControls = {
  toFormat: FormControl<string | null>;
};

type EditorHistoryControls = {
  cursor: FormControl<number>;
};

type EditorUiControls = {
  activeMode: FormControl<EditMode>;
  dirty: FormControl<boolean>;
  loading: FormControl<boolean>;
  error: FormControl<AppError | null>;
};

type EditorFormControls = {
  source: FormGroup<EditorSourceImageControls>;
  output: FormGroup<EditorOutputControls>;
  draft: FormControl<EditorDraftOperation | null>;
  committedEdits: FormControl<EditorCommittedEdit[]>;
  history: FormGroup<EditorHistoryControls>;
  ui: FormGroup<EditorUiControls>;
};


@Injectable()
export class EditorFormStore {
  readonly form = this.createForm();

  private readonly stateSubject = new ReplaySubject<EditorFormValue>(1);
  private nextCommitId = 0;
  private nextAnimationFrameId = 0;
  private nextMergeImageId = 0;

  /**
   * Animation frames and merge images are expensive for the user to assemble,
   * so when the mode switches away the draft is parked here instead of being
   * dropped and is restored transparently by ensure*Draft() on return.
   */
  private parkedAnimationDraft: EditorAnimationDraftOperation | null = null;
  private parkedMergeDraft: EditorMergeDraftOperation | null = null;

  readonly state$: Observable<EditorFormValue> = this.stateSubject.asObservable();

  constructor() {
    this.form.valueChanges
      .pipe(startWith(this.form.getRawValue()))
      .subscribe(() => {
        this.stateSubject.next(this.snapshot());
      });
  }

  snapshot(): EditorFormValue {
    return cloneEditorFormValue(this.form.getRawValue());
  }

  getCanvasViewState(): EditorCanvasViewState {
    return getEditorCanvasViewState(this.snapshot());
  }

  getAppliedImageSize() {
    return getAppliedImageSize(this.snapshot());
  }

  getDraft(): EditorDraftOperation | null {
    return cloneDraftOperation(this.form.controls.draft.getRawValue());
  }

  reset(): void {
    this.form.setValue(createEmptyEditorFormValue());
    this.nextCommitId = 0;
    this.nextAnimationFrameId = 0;
    this.nextMergeImageId = 0;
    this.parkedAnimationDraft = null;
    this.parkedMergeDraft = null;
  }

  setSourceImage(file: FileResult | null): void {
    this.form.setValue({
      ...createEmptyEditorFormValue(),
      source: createEditorSourceImageState(file),
    });
    this.nextAnimationFrameId = 0;
    this.nextMergeImageId = 0;
    this.parkedAnimationDraft = null;
    this.parkedMergeDraft = null;
  }

  setSourcePreview(previewUrl: string | null): void {
    this.form.controls.source.controls.previewUrl.setValue(previewUrl);
  }

  setSourceDimensions(width: number | null, height: number | null): void {
    this.form.controls.source.controls.width.setValue(width);
    this.form.controls.source.controls.height.setValue(height);
  }

  setOutputFormat(toFormat: string | null): void {
    this.form.controls.output.controls.toFormat.setValue(toFormat);
  }

  setActiveMode(mode: EditMode): void {
    this.form.controls.ui.controls.activeMode.setValue(mode);

    const draft = this.form.controls.draft.getRawValue();
    if (mode === EditMode.None || (draft && draft.mode !== mode)) {
      if (draft?.kind === 'animation' && mode !== EditMode.Animation) {
        this.parkedAnimationDraft = cloneDraftOperation(draft) as EditorAnimationDraftOperation;
      }
      if (draft?.kind === 'merge' && mode !== EditMode.Merge) {
        this.parkedMergeDraft = cloneDraftOperation(draft) as EditorMergeDraftOperation;
      }
      this.clearDraft();
    }
  }

  setLoading(loading: boolean): void {
    this.form.controls.ui.controls.loading.setValue(loading);
  }

  setError(error: AppError | null): void {
    this.form.controls.ui.controls.error.setValue(error);
  }

  setDraftCandidate(candidate: EditorDraftOperation | null): void {
    const draft = toEditorDraftOperation(
      candidate,
      this.form.controls.draft.getRawValue(),
    );
    this.form.controls.draft.setValue(draft);
    this.syncDirtyFlag();
  }

  setDraftCropRect(rect: { x: number; y: number; width: number; height: number } | null): void {
    const draft = withCropDraftRect(this.form.controls.draft.getRawValue(), rect);
    this.form.controls.draft.setValue(draft);
    this.syncDirtyFlag();
  }

  ensureAnimationDraft(): EditorAnimationDraftOperation | null {
    const draft = this.form.controls.draft.getRawValue();

    if (draft?.kind === 'animation') {
      // The draft may have been created before the source dimensions arrived
      // (width/height default to 1) — adopt the real size once it is known.
      if (draft.params.width <= 1 && draft.params.height <= 1) {
        const knownSize = this.getAppliedImageSize();
        if (knownSize && (knownSize.width > 1 || knownSize.height > 1)) {
          const resized: EditorAnimationDraftOperation = {
            ...draft,
            params: {
              ...draft.params,
              width: Math.max(1, Math.round(knownSize.width)),
              height: Math.max(1, Math.round(knownSize.height)),
            },
          };
          this.form.controls.draft.setValue(resized);
          this.syncDirtyFlag();
          return resized;
        }
      }
      return draft;
    }

    if (this.parkedAnimationDraft) {
      const restored = this.parkedAnimationDraft;
      this.parkedAnimationDraft = null;
      this.form.controls.draft.setValue(restored);
      this.syncDirtyFlag();
      return restored;
    }

    const source = this.form.controls.source.getRawValue();
    if (!source.sourceBase64) {
      return null;
    }

    const size = this.getAppliedImageSize();
    const animationDraft: EditorAnimationDraftOperation = {
      kind: 'animation',
      mode: EditMode.Animation,
      params: {
        width: Math.max(1, Math.round(size?.width ?? source.width ?? 1)),
        height: Math.max(1, Math.round(size?.height ?? source.height ?? 1)),
        backgroundColor: null,
        loopCount: 0,
        frames: [
          {
            id: 'animation-source-frame',
            name: source.fileName || 'Image',
            sourceBase64: source.sourceBase64,
            previewUrl: source.previewUrl ?? `data:${source.mimeType || 'image/png'};base64,${source.sourceBase64}`,
            mimeType: source.mimeType,
            durationMs: 500,
          },
        ],
      },
    };

    this.form.controls.draft.setValue(animationDraft);
    this.syncDirtyFlag();
    return animationDraft;
  }

  updateAnimationDraftSettings(
    params: Partial<Pick<EditorAnimationDraftOperation['params'], 'width' | 'height' | 'backgroundColor' | 'loopCount'>>,
  ): void {
    this.updateAnimationDraft(draft => ({
      ...draft,
      params: {
        ...draft.params,
        ...params,
      },
    }));
  }

  addAnimationFrames(frames: readonly Omit<EditorAnimationFrame, 'id'>[]): void {
    this.updateAnimationDraft(draft => ({
      ...draft,
      params: appendAnimationFrames(draft.params, frames, () => this.createAnimationFrameId()),
    }));
  }

  updateAnimationFrameDuration(id: string, durationMs: number): void {
    this.updateAnimationDraft(draft => ({
      ...draft,
      params: setAnimationFrameDuration(draft.params, id, durationMs),
    }));
  }

  moveAnimationFrame(id: string, position: number): void {
    this.updateAnimationDraft(draft => ({
      ...draft,
      params: moveAnimationFrame(draft.params, id, position),
    }));
  }

  duplicateAnimationFrame(id: string): void {
    this.updateAnimationDraft(draft => ({
      ...draft,
      params: duplicateAnimationFrame(draft.params, id, () => this.createAnimationFrameId()),
    }));
  }

  deleteAnimationFrame(id: string): void {
    this.updateAnimationDraft(draft => ({
      ...draft,
      params: deleteAnimationFrame(draft.params, id),
    }));
  }

  private createAnimationFrameId(): string {
    return `animation-frame-${++this.nextAnimationFrameId}`;
  }

  ensureMergeDraft(): EditorMergeDraftOperation | null {
    const draft = this.form.controls.draft.getRawValue();

    if (draft?.kind === 'merge') {
      return draft;
    }

    if (this.parkedMergeDraft) {
      const restored = this.parkedMergeDraft;
      this.parkedMergeDraft = null;
      this.form.controls.draft.setValue(restored);
      this.syncDirtyFlag();
      return restored;
    }

    const source = this.form.controls.source.getRawValue();
    if (!source.sourceBase64) {
      return null;
    }

    const mergeDraft: EditorMergeDraftOperation = {
      kind: 'merge',
      mode: EditMode.Merge,
      params: {
        layout: 'grid',
        rowCount: 1,
        columnCount: 2,
        spacing: 0,
        borderWidth: 0,
        borderColor: '#000000',
        backgroundColor: null,
        images: [
          {
            id: 'merge-source-image',
            name: source.fileName || 'Image',
            sourceBase64: source.sourceBase64,
            previewUrl: source.previewUrl ?? `data:${source.mimeType || 'image/png'};base64,${source.sourceBase64}`,
            mimeType: source.mimeType,
            x: 0,
            y: 0,
          },
        ],
      },
    };

    this.form.controls.draft.setValue(mergeDraft);
    this.syncDirtyFlag();
    return mergeDraft;
  }

  updateMergeDraftSettings(
    params: Partial<Omit<EditorMergeParams, 'images'>>,
  ): void {
    this.updateMergeDraft(draft => ({
      ...draft,
      params: {
        ...draft.params,
        ...params,
      },
    }));
  }

  addMergeImages(images: readonly Omit<EditorMergeImage, 'id'>[]): void {
    this.updateMergeDraft(draft => ({
      ...draft,
      params: appendMergeImages(draft.params, images, () => this.createMergeImageId()),
    }));
  }

  moveMergeImage(id: string, position: number): void {
    this.updateMergeDraft(draft => ({
      ...draft,
      params: moveMergeImage(draft.params, id, position),
    }));
  }

  duplicateMergeImage(id: string): void {
    this.updateMergeDraft(draft => ({
      ...draft,
      params: duplicateMergeImage(draft.params, id, () => this.createMergeImageId()),
    }));
  }

  deleteMergeImage(id: string): void {
    this.updateMergeDraft(draft => ({
      ...draft,
      params: deleteMergeImage(draft.params, id),
    }));
  }

  setMergeImagePosition(id: string, x: number, y: number): void {
    this.updateMergeDraft(draft => ({
      ...draft,
      params: setMergeImagePosition(draft.params, id, x, y),
    }));
  }

  setMergeImagePositions(positions: ReadonlyMap<string, { x: number; y: number }>): void {
    this.updateMergeDraft(draft => ({
      ...draft,
      params: setMergeImagePositions(draft.params, positions),
    }));
  }

  getMergeDraftSnapshot(): EditorMergeDraftOperation | null {
    const draft = this.form.controls.draft.getRawValue();
    if (draft?.kind === 'merge') {
      return cloneDraftOperation(draft) as EditorMergeDraftOperation;
    }
    return this.parkedMergeDraft
      ? (cloneDraftOperation(this.parkedMergeDraft) as EditorMergeDraftOperation)
      : null;
  }

  private createMergeImageId(): string {
    return `merge-image-${++this.nextMergeImageId}`;
  }

  private updateMergeDraft(
    update: (draft: EditorMergeDraftOperation) => EditorMergeDraftOperation,
  ): void {
    const draft = this.ensureMergeDraft();

    if (!draft) {
      return;
    }

    this.form.controls.draft.setValue(update(draft));
    this.syncDirtyFlag();
  }

  /** Active or parked animation draft — whatever should survive the session. */
  getAnimationDraftSnapshot(): EditorAnimationDraftOperation | null {
    const draft = this.form.controls.draft.getRawValue();
    if (draft?.kind === 'animation') {
      return cloneDraftOperation(draft) as EditorAnimationDraftOperation;
    }
    return this.parkedAnimationDraft
      ? (cloneDraftOperation(this.parkedAnimationDraft) as EditorAnimationDraftOperation)
      : null;
  }

  /** Restores an animation draft saved by a previous editor instance. */
  restoreAnimationDraft(draft: EditorAnimationDraftOperation): void {
    const restored = cloneDraftOperation(draft) as EditorAnimationDraftOperation;
    this.form.controls.draft.setValue(restored);
    this.form.controls.ui.controls.activeMode.setValue(EditMode.Animation);

    // Keep the id counter ahead of restored frame ids to avoid collisions.
    const maxRestoredId = restored.params.frames.reduce((max, frame) => {
      const match = /^animation-frame-(\d+)$/.exec(frame.id);
      return match ? Math.max(max, Number(match[1])) : max;
    }, 0);
    this.nextAnimationFrameId = Math.max(this.nextAnimationFrameId, maxRestoredId);

    this.syncDirtyFlag();
  }

  clearDraft(): void {
    this.form.controls.draft.setValue(null);
    this.syncDirtyFlag();
  }

  prepareDraftCommit(): EditorPreparedCommit | null {
    const id = `edit-${this.nextCommitId + 1}`;
    const preparedCommit = toPreparedCommit(id, this.form.controls.draft.getRawValue());

    if (preparedCommit) {
      this.nextCommitId += 1;
    }

    return preparedCommit;
  }

  executePreparedCommit(preparedCommit: EditorPreparedCommit): void {
    const committedEdits = cloneCommittedEdits(
      this.form.controls.committedEdits.getRawValue(),
    );
    const cursor = this.form.controls.history.controls.cursor.getRawValue();

    if (committedEdits[cursor]?.id === preparedCommit.id) {
      this.form.controls.history.controls.cursor.setValue(cursor + 1);
    } else {
      const nextEdits = committedEdits.slice(0, cursor);
      nextEdits.push(preparedCommit.edit);
      this.form.controls.committedEdits.setValue(nextEdits);
      this.form.controls.history.controls.cursor.setValue(nextEdits.length);
    }

    this.form.controls.draft.setValue(null);
    this.syncDirtyFlag();
  }

  rollbackPreparedCommit(preparedCommitId: string): void {
    const committedEdits = this.form.controls.committedEdits.getRawValue();
    const cursor = this.form.controls.history.controls.cursor.getRawValue();
    const committedIndex = committedEdits.findIndex(edit => edit.id === preparedCommitId);

    if (committedIndex < 0 || committedIndex >= cursor) {
      return;
    }

    this.form.controls.history.controls.cursor.setValue(committedIndex);
    this.syncDirtyFlag();
  }

  private syncDirtyFlag(): void {
    const cursor = this.form.controls.history.controls.cursor.getRawValue();
    const draft = this.form.controls.draft.getRawValue();
    // A bare animation/merge draft holds only the auto-added source image —
    // nothing has been edited yet, so it must not count as a change.
    let hasMeaningfulDraft = Boolean(draft);
    if (draft?.kind === 'animation') {
      hasMeaningfulDraft = draft.params.frames.length > 1;
    } else if (draft?.kind === 'merge') {
      hasMeaningfulDraft = isMeaningfulMergeDraft(draft);
    }
    this.form.controls.ui.controls.dirty.setValue(hasMeaningfulDraft || cursor > 0);
  }

  private updateAnimationDraft(
    update: (draft: EditorAnimationDraftOperation) => EditorAnimationDraftOperation,
  ): void {
    const draft = this.ensureAnimationDraft();

    if (!draft) {
      return;
    }

    this.form.controls.draft.setValue(update(draft));
    this.syncDirtyFlag();
  }

  private createForm(): FormGroup<EditorFormControls> {
    const initial = createEmptyEditorFormValue();

    return new FormGroup<EditorFormControls>({
      source: new FormGroup<EditorSourceImageControls>({
        fileId: new FormControl(initial.source.fileId),
        fileName: new FormControl(initial.source.fileName, { nonNullable: true }),
        sourceBase64: new FormControl(initial.source.sourceBase64),
        mimeType: new FormControl(initial.source.mimeType),
        previewUrl: new FormControl(initial.source.previewUrl),
        width: new FormControl(initial.source.width),
        height: new FormControl(initial.source.height),
      }),
      output: new FormGroup<EditorOutputControls>({
        toFormat: new FormControl(initial.output.toFormat),
      }),
      draft: new FormControl(initial.draft),
      committedEdits: new FormControl(initial.committedEdits, { nonNullable: true }),
      history: new FormGroup<EditorHistoryControls>({
        cursor: new FormControl(initial.history.cursor, { nonNullable: true }),
      }),
      ui: new FormGroup<EditorUiControls>({
        activeMode: new FormControl(initial.ui.activeMode, { nonNullable: true }),
        dirty: new FormControl(initial.ui.dirty, { nonNullable: true }),
        loading: new FormControl(initial.ui.loading, { nonNullable: true }),
        error: new FormControl(initial.ui.error),
      }),
    });
  }
}
